// 🔧 guides.js
// ✅ Purpose: Public-facing guide library for Let's Go Vote
// ✅ Features:
// - Shows step-by-step learning paths for event managers
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Highlights voting, entries, QR codes, sponsors, volunteers, auctions, and security
// - Designed to help future customers understand the app capabilities
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo_head";

function Guides() {
  const guideSections = [
    {
      title: "Getting Started",
      icon: "🚀",
      description:
        "Learn the basic steps to create, configure, and publish your first event.",
      path: "/guides/getting-started",
    },
    {
      title: "Configure Voting",
      icon: "🗳️",
      description:
        "Understand People’s Choice, Ranked Choice, Judged Points, and Scorecard voting.",
      path: "/guides/configure-voting",
    },
    {
      title: "Categories & Entries",
      icon: "🚗",
      description:
        "Set up categories, add entries, review public submissions, and prepare your event for voting.",
      path: "/guides/categories-and-entries",
    },
    {
      title: "QR Codes",
      icon: "📱",
      description:
        "Use QR codes to make voting, entries, auctions, sponsors, and event access easier for attendees.",
      path: "/guides/qr-codes",
    },
    {
      title: "Running Event Day",
      icon: "🏁",
      description:
        "A practical checklist for opening voting, helping attendees, monitoring activity, and publishing winners.",
      path: "/guides/running-event-day",
    },
    {
      title: "Sponsors",
      icon: "⭐",
      description:
        "Create sponsor levels, sponsor pages, and sponsor awards that display on your public event pages.",
      path: "/guides/sponsors",
    },
    {
      title: "Volunteers",
      icon: "🙋",
      description:
        "Set up volunteer activities, time slots, assignments, and signup workflows for your event team.",
      path: "/guides/volunteers",
    },
    {
      title: "Silent Auctions",
      icon: "🏆",
      description:
        "Create auction items, collect bids, notify bidders, and manage auction results.",
      path: "/guides/silent-auctions",
    },
    {
      title: "Security & Trust",
      icon: "🛡️",
      description:
        "Understand authentication, email verification, permissions, audit records, and vote integrity.",
      path: "/guides/security-and-trust",
    },  
    {
      title: "Car Show Playbook",
      icon: "🚘",
      description:
        "Learn how to run a People's Choice car show with categories, entries, QR codes, voting, and winners.",
      path: "/guides/car-show-playbook",
    },
    {
      title: "Art Show Playbook",
      icon: "🎨",
      description:
        "Learn how to run an art show with artist entries, categories, public voting, judging, and awards.",
      path: "/guides/art-show-playbook",
    },
    {
      title: "Pet Show Playbook",
      icon: "🐾",
      description:
        "Learn how to run a pet contest with fun categories, public entries, QR voting, and winners.",
      path: "/guides/pet-show-playbook",
    },
    {
      title: "School Election Playbook",
      icon: "🏫",
      description:
        "Learn how to run school elections, student votes, club elections, and ranked-choice ballots.",
      path: "/guides/school-election-playbook",
    },   
    
  ];

  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="Manager Guides | Let's Go Vote"
        description="Step-by-step guides for setting up and running events with Let's Go Vote, including QR code voting, entries, sponsors, volunteers, auctions, and event day management."
        path="/guides"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Guides", path: "/guides" },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">📚 Manager Guides</h1>
          <p className="lead text-muted">
            Step-by-step guides to help event organizers set up, manage, and run
            successful events with Let&apos;s Go Vote.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

        {/* ✅ Guide Navigation */}
        <section className="mb-5">
          <div className="alert alert-secondary text-start mb-0">
            <h5 className="mb-3">📚 Manager Guides</h5>

            <div className="d-flex flex-wrap gap-2">
              <Link to="/guides/getting-started" className="btn btn-sm btn-outline-primary">
                🚀 Getting Started
              </Link>

              <Link to="/guides/configure-voting" className="btn btn-sm btn-outline-primary">
                🗳️ Voting
              </Link>

              <Link to="/guides/categories-and-entries" className="btn btn-sm btn-outline-primary">
                🚗 Categories & Entries
              </Link>

              <Link to="/guides/qr-codes" className="btn btn-sm btn-outline-primary">
                📱 QR Codes
              </Link>

              <Link to="/guides/running-event-day" className="btn btn-sm btn-outline-primary">
                🏁 Event Day
              </Link>

              <Link to="/guides/sponsors" className="btn btn-sm btn-outline-primary">
                ⭐ Sponsors
              </Link>

              <Link to="/guides/volunteers" className="btn btn-sm btn-outline-primary">
                🙋 Volunteers
              </Link>

              <Link to="/guides/silent-auctions" className="btn btn-sm btn-outline-primary">
                🏆 Auctions
              </Link>

              <Link to="/guides/security-and-trust" className="btn btn-sm btn-outline-primary">
                🛡️ Security
              </Link>

              <Link to="/guides" className="btn btn-sm btn-primary">
                📚 All Guides
              </Link>
            </div>
          </div>
        </section>

        <section className="mb-5">
          <div className="alert alert-info text-start mb-4">
            <h5 className="mb-3">👋 Learn how to run your event</h5>

            <p className="mb-3">
              These guides are designed for event managers who want to understand
              how Let&apos;s Go Vote works from setup through event day.
            </p>

            <div className="d-flex flex-column flex-md-row gap-2">
              <Link to="/help" className="btn btn-primary">
                Visit Help Center
              </Link>

              <Link to="/request_event" className="btn btn-outline-primary">
                📬 Request to use Let&apos;s Go Vote
              </Link>
            </div>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Guide Library</h3>

          <div className="row g-3">
            {guideSections.map((guide) => (
              <div className="col-12 col-md-6 col-lg-4" key={guide.title}>
                <div className="border rounded p-3 h-100 bg-light">
                  <h5 className="fw-bold">
                    {guide.icon} {guide.title}
                  </h5>

                  <p className="text-muted">{guide.description}</p>

                  <Link
                    to={guide.path}
                    className="btn btn-sm btn-outline-primary"
                  >
                    Read Guide
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Recommended setup path</h5>

            <ol className="mb-0">
              <li>Create your event and confirm your event details.</li>
              <li>Choose the voting type that fits your event.</li>
              <li>Add categories and entries.</li>
              <li>Print or share QR codes.</li>
              <li>Test voting before event day.</li>
              <li>Open voting during the event.</li>
              <li>Review results and publish winners.</li>
            </ol>
          </div>
        </section>
      </div>
    </section>
  );
}

export default Guides;